/* eslint-disable */
// AnnounceButton — the megaphone that opens the composer pre-attached.
//
// It exists as its own file for two reasons, and the second is the load-bearing
// one. (1) The dev card and the originals card were doing the SAME three things
// — gate on the portal, build a {kind,id,code,name,image} seed, open the
// composer — so a second copy was already drifting. (2) `DevCardNew.js` is
// grandfathered at 438 lines (Convention #6 / GOV1 H2): a grandfathered file may
// shrink but never grow, so the inline version was correctly BLOCKED at commit.
// The block named the real problem — a new component inside an existing file
// (Convention #7) — rather than a number to work around.
//
// ⚠️ THE SEED IS BUILT ON CLICK, NOT ON RENDER. `makeSeed` is a function, so a
// card that renders a thousand times pays nothing, and `getDevPrimaryImage`
// never runs for a viewer who cannot send. Passing a prebuilt object would
// compute it on every render of every card, including the ones that render no
// button at all.
//
// ⚠️ THE GATE HERE IS CONVENIENCE, NOT THE BOUNDARY. `useCanAnnounce` hides a
// button that would always fail; the SERVER still decides who may send (P3's
// rule). Never treat this hook as the permission check.
//
// Chrome is the caller's: `className` comes from the host card's own module, so
// the button is visually one of that card's controls rather than a foreign body
// (Convention #139 — shared chrome stays prop-driven).

import React from 'react';
import { useCanAnnounce } from '../../hooks/useAnnouncementAudience';
import { useNotificationsStore } from '../../stores/notificationsStore';

// Lucide `megaphone` — a cone with a handle. NOT volume-2 (a speaker with
// arcs), which was shipped into a prototype once already and had to be undone.
const PATHS = (
  <>
    <path d="m3 11 18-5v12L3 14v-3z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </>
);

export default function AnnounceButton({ portal, makeSeed, className, dataTest, size }) {
  const canAnnounce = useCanAnnounce(portal);
  const setShowComposer = useNotificationsStore((st) => st.setShowComposer);
  if (!canAnnounce) return null;

  // No width/height when `size` is absent: the dev card sizes its tools from
  // `.ncTool svg`, and hard-coding a number there would override its own scale.
  const dims = size ? { width: size, height: size } : {};

  return (
    <button
      type="button"
      className={className}
      onClick={(e) => {
        e.stopPropagation();
        setShowComposer(true, makeSeed());
      }}
      aria-label="Announce this"
      title="Announce this"
      data-test={dataTest}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" {...dims}>{PATHS}</svg>
    </button>
  );
}
